import {
  AnnouncementMessage,
  ICreditClassData,
  UserTrackerData,
  UserTrackerDataRequest
} from '@/@types/client-api';
import { DismissableBannerProps } from '@/components/blocks/DismissableBannerBlock';
import { PayloadAction, UnknownAction, createSlice } from '@reduxjs/toolkit';
import creditTrackerEndpoints from '../api/client/creditTracker';
import userLicensesEndpoints from '../api/client/userLicenses';
import { isAnyOf } from '@reduxjs/toolkit';
export interface CreditTrackerState {
  pendingRequests: number;
  latestTrackerRequestIds: Record<number, string>;
  request: UserTrackerDataRequest[] | null;
  selectedRegion?: Record<number, UserTrackerData>;
  trackers: Record<number, Array<UserTrackerData>>;
  providers: Array<string>;
  announcements: Record<string, Array<AnnouncementMessage>>;
  creditTypes: Array<ICreditClassData>;
  globalBanners?: Record<string, DismissableBannerProps[] | null> | null;
}

const initialState: CreditTrackerState = {
  pendingRequests: 0,
  latestTrackerRequestIds: {},
  request: null,
  selectedRegion: undefined,
  trackers: {},
  providers: [],
  creditTypes: [],
  announcements: {}
};

// Blocking mutation pending matcher for controlling global loading state.
const pending = isAnyOf(
  creditTrackerEndpoints.endpoints.switchStatus.matchPending,
  creditTrackerEndpoints.endpoints.updateUserCleOptions.matchPending
);

// Blocking mutation settled matcher for controlling global loading state.
const settledRequest = isAnyOf(
  creditTrackerEndpoints.endpoints.switchStatus.matchFulfilled,
  creditTrackerEndpoints.endpoints.switchStatus.matchRejected,

  creditTrackerEndpoints.endpoints.updateUserCleOptions.matchFulfilled,
  creditTrackerEndpoints.endpoints.updateUserCleOptions.matchRejected
);

const settled = (action: UnknownAction) =>
  settledRequest(action) && !(action.meta as { condition?: boolean } | undefined)?.condition;

export const creditTrackerSlice = createSlice({
  name: 'creditTracker',
  initialState,
  reducers: {
    creditTrackerLoading: (state, payload: PayloadAction<boolean>) => {
      if (payload.payload) state.pendingRequests++;
      else state.pendingRequests = Math.max(0, state.pendingRequests - 1);
    },
    creditTrackerDataSuccess: (
      state,
      action: PayloadAction<{
        request: UserTrackerDataRequest[];
        response: Array<Record<string, Array<UserTrackerData>>> | null;
      }>
    ) => {
      for (const trackers of action.payload.response ?? []) {
        for (const key in trackers) {
          const parsedKey = parseInt(key);
          state.trackers[parsedKey] = trackers[parsedKey];
        }
      }
      state.request = action.payload.request;
    },
    creditTrackerUpdateData: (state, action: PayloadAction<UserTrackerData | undefined>) => {
      if (!action.payload?.customerId) return;
      const userTracker = state.trackers[action.payload.customerId];

      state.trackers[action.payload.customerId] = userTracker?.map(a => {
        if (a.creditRegion_SK == action.payload?.creditRegion_SK) {
          return action.payload!;
        }
        return a;
      });
    },
    creditTrackerSetSelectedRegion: (state, action: PayloadAction<UserTrackerData | undefined>) => {
      updateSelectedRegion(state, action.payload);
    },
    creditTrackerGetCreditTypesSuccess: (state, action: PayloadAction<Array<ICreditClassData>>) => {
      state.creditTypes = action.payload;
    },
    creditTrackerProvidersSuccess: (state, action: PayloadAction<Array<string>>) => {
      state.providers = action.payload;
    },
    creditTrackerSetGlobalBanners: (
      state,
      action: PayloadAction<Record<string, DismissableBannerProps[]>>
    ) => {
      state.globalBanners ??= {};

      for (const key in action.payload) {
        state.globalBanners[key] = action.payload[key];
      }
    },
    creditTrackerRemoveGlobalBanners: (state, action: PayloadAction<string>) => {
      delete state.globalBanners?.[action.payload];
    }
  },
  extraReducers: builder => {
    builder
      .addMatcher(pending, state => {
        state.pendingRequests++;
      })
      .addMatcher(
        creditTrackerEndpoints.endpoints.getCreditTracker.matchPending,
        (state, action) => {
          action.meta.arg.originalArgs?.forEach(request => {
            if (request.individualSk !== undefined) {
              state.latestTrackerRequestIds[request.individualSk] = action.meta.requestId;
            }
          });
        }
      )
      .addMatcher(settled, state => {
        state.pendingRequests = Math.max(0, state.pendingRequests - 1);
      })
      .addMatcher(
        creditTrackerEndpoints.endpoints.getCreditTracker.matchFulfilled,
        (state, action) => {
          const currentRequests = action.meta.arg.originalArgs?.filter(
            request =>
              request.individualSk !== undefined &&
              state.latestTrackerRequestIds[request.individualSk] === action.meta.requestId
          );
          if (!currentRequests?.length) return;

          for (const trackers of action.payload?.trackerUsers ?? []) {
            for (const key in trackers) {
              const parsedKey = parseInt(key);
              if (currentRequests.some(request => request.individualSk === parsedKey)) {
                state.trackers[parsedKey] = trackers[parsedKey];
              }
            }
          }
          state.request = currentRequests;

          currentRequests.forEach(request => {
            const individualSk = request.individualSk!;
            const currentSelectedRegion = state.selectedRegion?.[individualSk];
            const updatedSelectedRegion = action.payload?.trackerUsers
              ?.find(a => !!a?.[individualSk.toString()])
              ?.[individualSk.toString()]?.find(
                a => a.creditRegion_SK === currentSelectedRegion?.creditRegion_SK
              );

            if (updatedSelectedRegion) {
              updateSelectedRegion(state, updatedSelectedRegion);
            }
          });
        }
      )
      .addMatcher(
        creditTrackerEndpoints.endpoints.getManyAnnouncementMessages.matchFulfilled,
        (state, action) => {
          for (const key in action.payload) {
            if (key?.trim()?.length) {
              state.announcements[key] = action.payload[key];
            }
          }
        }
      )
      .addMatcher(userLicensesEndpoints.endpoints.deleteLicense.matchFulfilled, state =>
        resetState(state)
      )
      .addMatcher(userLicensesEndpoints.endpoints.postLicense.matchFulfilled, state =>
        resetState(state)
      )
      .addMatcher(userLicensesEndpoints.endpoints.updateLicense.matchFulfilled, state =>
        resetState(state)
      );
  }
});

function resetState(state: CreditTrackerState): CreditTrackerState {
  return {
    ...initialState,
    pendingRequests: state.pendingRequests
  };
}

function updateSelectedRegion(state: CreditTrackerState, data: UserTrackerData | undefined) {
  const val = { ...data } as UserTrackerData;
  if (val.customerId) {
    if (state.selectedRegion) state.selectedRegion[val.customerId] = val;
    else state.selectedRegion = { [val.customerId]: val };
  }
}

export const {
  creditTrackerLoading,
  creditTrackerDataSuccess,
  creditTrackerUpdateData,
  creditTrackerGetCreditTypesSuccess,
  creditTrackerSetSelectedRegion,
  creditTrackerProvidersSuccess,
  creditTrackerSetGlobalBanners,
  creditTrackerRemoveGlobalBanners
} = creditTrackerSlice.actions;

export default creditTrackerSlice.reducer;
